import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import { Confetti, Modal, Page, Petals, Reveal } from '../components/UI';
import { useApp } from '../context/AppContext';
import { api } from '../utils/api';

const COMPARE = [
  ['Create profile & get matches', true, true],
  ['Send unlimited interests', true, true],
  ['Kundli match score', true, true],
  ['Chat after interest is accepted', true, true],
  ['Message anyone directly', false, true],
  ['View verified contact numbers', false, true],
  ['See who viewed your profile', false, true],
  ['See Premium-only photos & income', false, true],
  ['Incognito browsing', false, true],
];

export default function Premium() {
  const { meta, user, isPremium, setUser, toast } = useApp();
  const navigate = useNavigate();
  const [chosen, setChosen] = useState(null);
  const [busy, setBusy] = useState(false);
  const [party, setParty] = useState(false);
  const plans = meta?.plans || [];

  const pick = (p) => {
    if (!user) return navigate('/login?next=/premium');
    return setChosen(p);
  };

  const pay = async () => {
    setBusy(true);
    try {
      const r = await api('/plans/subscribe', { method: 'POST', body: { planId: chosen.id } });
      setUser(r.user);
      setChosen(null);
      setParty(true);
      toast(r.message, 'success');
      setTimeout(() => setParty(false), 2500);
    } catch (e) {
      toast(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Page className="premium">
      <Confetti run={party} />
      <section className="premium__hero">
        <Petals count={10} />
        <motion.span className="premium__crown" animate={{ y: [0, -8, 0], rotate: [0, 6, -4, 0] }} transition={{ repeat: Infinity, duration: 4 }}>
          <Icon name="crown" size={44} />
        </motion.span>
        <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>Find your person, <em>faster</em></motion.h1>
        <p>Premium members connect 4× more often. Pick the plan that fits your journey.</p>
        {isPremium && (
          <span className="pill pill--gold">
            <Icon name="crown" size={13} /> You're on {user.premium.plan.replace(/^./, (c) => c.toUpperCase())} till {new Date(user.premium.expiresAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })} · {user.premium.contactsLeft} contact views left
          </span>
        )}
      </section>

      <div className="container">
        <div className="plans">
          {plans.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.1}>
              <motion.div className={`plan ${p.popular ? 'plan--popular' : ''}`} whileHover={{ y: -10 }} style={{ '--plan': p.color }}>
                {p.popular && <span className="plan__ribbon">Most loved</span>}
                <h3>{p.name}</h3>
                <div className="plan__price">₹{p.price.toLocaleString('en-IN')}<small> / {p.months} months</small></div>
                <p className="plan__per">≈ ₹{Math.round(p.price / p.months).toLocaleString('en-IN')} per month</p>
                <ul>
                  {p.features.map((f) => <li key={f}><Icon name="check" size={15} stroke={2.4} />{f}</li>)}
                </ul>
                <motion.button whileTap={{ scale: 0.96 }} className={`btn btn--block ${p.popular ? 'btn--primary shine' : 'btn--ghost'}`} onClick={() => pick(p)}>
                  {isPremium ? 'Extend with' : 'Choose'} {p.name}
                </motion.button>
              </motion.div>
            </Reveal>
          ))}
        </div>

        <Reveal className="compare">
          <h2>Free vs Premium</h2>
          <div className="compare__table">
            <div className="compare__row compare__row--head"><span /> <strong>Free</strong> <strong>Premium</strong></div>
            {COMPARE.map(([f, free, prem]) => (
              <div key={f} className="compare__row">
                <span>{f}</span>
                <span>{free ? <Icon name="check" size={17} stroke={2.4} className="fit--yes" /> : <Icon name="x" size={16} className="fit--no" />}</span>
                <span><Icon name="check" size={17} stroke={2.4} className={prem ? 'fit--yes' : 'fit--no'} /></span>
              </div>
            ))}
          </div>
        </Reveal>
        <p className="center muted small">Questions? <Link to="/help">Visit our help centre</Link> or call +91 1800 120 4545.</p>
      </div>

      <Modal open={!!chosen} onClose={() => setChosen(null)} title={`Checkout · ${chosen?.name}`}>
        {chosen && (
          <div className="checkout">
            <div className="checkout__sum">
              <span>{chosen.name} · {chosen.months} months</span>
              <strong>₹{chosen.price.toLocaleString('en-IN')}</strong>
            </div>
            <div className="checkout__sum muted"><span>GST included</span><span>—</span></div>
            <div className="note"><Icon name="shield" size={16} /> Demo checkout: no payment is taken. Connect Razorpay or Stripe on the server before launch.</div>
            <motion.button whileTap={{ scale: 0.96 }} className="btn btn--primary btn--block btn--lg" onClick={pay} disabled={busy}>
              {busy ? 'Activating…' : `Pay ₹${chosen.price.toLocaleString('en-IN')}`}
            </motion.button>
          </div>
        )}
      </Modal>
    </Page>
  );
}
